import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription, combineLatest} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {MethodsToValueUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    MethodsToValue,
    MethodsToValueOnServerCreated,
    selectMethodsToValuesActionLoading,
    selectMethodsToValueById,
    selectLastCreatedMethodsToValueId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-methods-to-value-edit',
    templateUrl: './methods-to-value-edit.component.html',
    styleUrls: ['./methods-to-value-edit.component.scss']
})


export class MethodsToValueEditComponent implements OnInit, OnDestroy {

    model: MethodsToValue;
    oldModel: MethodsToValue;

    methodsToValueForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    readonly = false

    /**
     * Component constructor
     *
     * @param subheaderService
     * @param activatedRoute
     * @param router
     * @param fb: FormBuilder
     * @param layoutUtilsService
     * @param store: Store<AppState>
     * @param layoutConfigService
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectMethodsToValuesActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectMethodsToValueById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initMethodsToValue();
                    }
                });
            } else {
                this.model = new MethodsToValue();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initMethodsToValue();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Methods To Valuess', page: `../default/admin-management/linked-tables/methods-to-values`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/methods-to-values/edit/${id}`
                    : `../default/admin-management/linked-tables/methods-to-values/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initMethodsToValue() {
        this.createForm();
    }


    createForm() {
        this.methodsToValueForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('METHODS_TO_VALUE.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('METHODS_TO_VALUE.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.methodsToValueForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.methodsToValueForm.markAsPristine();
        this.methodsToValueForm.markAsUntouched();
        this.methodsToValueForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.methodsToValueForm.controls;
        /** check form */
        if (this.methodsToValueForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateMethodsToValue(this.model);
        } else {
            this.createMethodsToValue(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: MethodsToValueModel
     */
    updateMethodsToValue(_model: MethodsToValue) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateMethodsToValue: Update<MethodsToValue> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new MethodsToValueUpdated({
            partialmethodsToValue: updateMethodsToValue,
            methodsToValue: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: MethodsToValueModel
     */
    createMethodsToValue(_model: MethodsToValue) {
        this.store.dispatch(new MethodsToValueOnServerCreated({methodsToValue: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedMethodsToValueId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0
            && this.model.prefix && this.model.prefix.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }


    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name
                || this.model.prefix != this.oldModel.prefix;
        }
        return false;
    }

}
